import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser, FaEdit, FaTrash, FaChartLine } from 'react-icons/fa'
import { Modal, Button } from '../../../../UI'
import { getMerchantStats, deleteMerchant } from '../../../../store/actions/merchants'
import MerchantForm from '../MerchantForm'
import Container from './styles'

const MerchantDetails = ({ show, handleClose, merchant }) => {
  const dispatch = useDispatch()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [stats, setStats] = useState(null)
  const [loadingStats, setLoadingStats] = useState(false)

  useEffect(() => {
    if (merchant && show) {
      loadMerchantStats()
    }
  }, [merchant, show])

  const loadMerchantStats = async () => {
    setLoadingStats(true)
    const result = await dispatch(getMerchantStats(merchant.id))
    if (result.success) {
      setStats(result.data)
    }
    setLoadingStats(false)
  }

  const handleEdit = () => {
    setShowEditModal(true)
  }

  const handleEditClose = () => {
    setShowEditModal(false)
    // Reload merchant stats after edit
    if (merchant) {
      loadMerchantStats()
    }
  }

  const handleDelete = async () => {
    const result = await dispatch(deleteMerchant(merchant.id))
    if (result.success) {
      setShowDeleteConfirm(false)
      handleClose()
    }
  }

  if (!merchant) return null

  return (
    <>
      <Modal showModal={show} onClose={handleClose} showCloseBtn={true}>
        <Container>
          <div className="details-container">
            <header>
              <div className="header-content">
                <div className="merchant-icon">
                  <FaBuilding />
                </div>
                <div className="header-text">
                  <h2>{merchant.companyName}</h2>
                  <span className={`status-badge ${merchant.isActive ? 'active' : 'inactive'}`}>
                    {merchant.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="header-actions">
                <button className="btn-icon" onClick={handleEdit} title="Edit Merchant">
                  <FaEdit />
                </button>
                <button className="btn-icon btn-delete" onClick={() => setShowDeleteConfirm(true)} title="Delete Merchant">
                  <FaTrash />
                </button>
              </div>
            </header>

            <div className="info-section">
              <h3>Contact Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-icon">
                    <FaUser />
                  </div>
                  <div className="info-content">
                    <label>Contact Person</label>
                    <span>{merchant.contactPerson}</span>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-content">
                    <label>Email</label>
                    <span>{merchant.email}</span>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <FaPhone />
                  </div>
                  <div className="info-content">
                    <label>Phone</label>
                    <span>{merchant.phone}</span>
                  </div>
                </div>

                <div className="info-item full-width">
                  <div className="info-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="info-content">
                    <label>Address</label>
                    <span>{merchant.address}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h3>
                <FaChartLine /> Activity Statistics
              </h3>
              {loadingStats ? (
                <div className="loading-stats">Loading statistics...</div>
              ) : stats ? (
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalOrders || 0}</div>
                    <div className="stat-label">Total Orders</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.pendingOrders || 0}</div>
                    <div className="stat-label">Pending Orders</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.completedOrders || 0}</div>
                    <div className="stat-label">Completed Orders</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalRiders || 0}</div>
                    <div className="stat-label">Active Riders</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalStaff || 0}</div>
                    <div className="stat-label">Staff Members</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">
                      {stats.totalRevenue ? `₦${stats.totalRevenue.toLocaleString()}` : '₦0'}
                    </div>
                    <div className="stat-label">Total Revenue</div>
                  </div>
                </div>
              ) : (
                <div className="no-stats">No statistics available</div>
              )}
            </div>

            <div className="dates-section">
              <div className="date-item">
                <label>Created:</label>
                <span>{new Date(merchant.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              {merchant.updatedAt && (
                <div className="date-item">
                  <label>Last Updated:</label>
                  <span>{new Date(merchant.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              )}
            </div>
          </div>
        </Container>
      </Modal>

      {/* Edit Modal */}
      <MerchantForm
        show={showEditModal}
        handleClose={handleEditClose}
        editData={merchant}
      />

      {/* Delete Confirmation Modal */}
      <Modal showModal={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} showCloseBtn={true}>
        <div style={{ padding: '2rem', textAlign: 'center', background: 'white', borderRadius: '12px', color: '#1a1a1a' }}>
          <div style={{ fontSize: '3rem', color: '#dc3545', marginBottom: '1rem' }}>
            <FaTrash />
          </div>
          <h2 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Delete Merchant?</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            Are you sure you want to delete <strong>{merchant.companyName}</strong>? 
            This action cannot be undone and will remove all associated data.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
              Cancel
            </Button>
            <Button onClick={handleDelete} style={{ background: '#dc3545' }}>
              Delete Merchant
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default MerchantDetails
